/**
 * API 服务 - 基于公共 request 封装
 * 登录/注册不走 token，其余接口自动带 token
 */

import request from '../utils/request'

/** 登录请求体 */
export interface LoginParams {
  username: string
  password: string
}

/** 注册请求体 */
export interface RegisterParams {
  username: string
  email: string
  password: string
}

/** 接口返回的用户信息（按常见后端字段兼容） */
export interface ApiUser {
  id?: string | number
  username?: string
  name?: string
  email?: string
  nickname?: string
  phone?: string
  /** 角色编码列表，如 ["ADMIN"] */
  roles?: string[]
  avatarUrl?: string | null
  [key: string]: unknown
}

/** 登录接口 menuTree 节点（菜单与按钮权限树） */
export interface ApiMenuTreeNode {
  code: string
  name: string
  /** menu=导航菜单，button=按钮权限点 */
  type: 'menu' | 'button'
  path?: string
  permissionAction?: string
  children?: ApiMenuTreeNode[]
}

/** 登录接口 data 结构（新文档） */
export interface AuthLoginData {
  token: string
  expiresIn?: number
  user: ApiUser
  /** 当前用户可见的权限菜单树 */
  menuTree?: ApiMenuTreeNode[]
  /** 当前用户拥有的全部按钮权限 code 扁平列表，用于按钮显隐 */
  buttonPermissions?: string[]
}

/** 登录/注册返回（兼容新文档与旧格式） */
export interface AuthResponse {
  success?: boolean
  code?: string
  message?: string
  data?: AuthLoginData & { access_token?: string; user?: ApiUser; token?: string }
  access_token?: string
  token?: string
  user?: ApiUser
}

/** 后端 path 与前端路由映射（登录接口 menuTree 中 path 转前端路由） */
export const AUTH_MENU_PATH_MAP: Record<string, string> = {
  '/dashboard': '/',
  '/asset-products': '/assets',
  '/data-sources': '/data',
  '/analytics': '/analytics',
  '/settings': '/settings',
  '/account-management': '/accounts',
  '/role-management': '/roles',
  '/accounts': '/accounts',
  '/roles': '/roles',
  '/export-logs': '/export-logs',
  '/import-logs': '/import-logs',
  '/log-management': '/import-logs',
}

/** 菜单 code 与前端路由映射（用于侧栏点击跳转） */
export const MENU_CODE_TO_PATH: Record<string, string> = {
  dashboard: '/',
  asset_products: '/assets',
  data_sources: '/data',
  analytics: '/analytics',
  settings: '/settings',
  settings_profile: '/settings/profile',
  settings_security: '/settings/security',
  user_management: '/accounts',
  role_management: '/roles',
  accounts: '/accounts',
  roles: '/roles',
  import_logs: '/import-logs',
  export_logs: '/export-logs',
}

// ========== 认证接口（不带头部 token，由 request 拦截器自动跳过） ==========

export const authApi = {
  /** 登录：POST /api/auth/login */
  login: (data: LoginParams) =>
    request.post<AuthResponse>('/api/auth/login', data).catch((e) => {
      throw e
    }),

  /** 注册：POST /api/auth/register */
  register: (data: RegisterParams) =>
    request.post<AuthResponse>('/api/auth/register', data).catch((e) => {
      throw e
    }),
}

// ========== 以下接口均自动携带 token ==========

/** 仪表盘统计/活动：改为使用新的 POST 接口 */
export const dashboardApi = {
  /** POST /api/dashboard/summary - 仪表盘汇总指标 */
  summary: (body?: Record<string, unknown>) =>
    request.post('/api/dashboard/summary', { dummy: true, ...(body ?? {}) }),
  /** POST /api/dashboard/distribution - 仪表盘资产分布 */
  distribution: (body?: Record<string, unknown>) =>
    request.post('/api/dashboard/distribution', { dummy: true, ...(body ?? {}) }),
  /**
   * POST /api/dashboard/recent-activities - 仪表盘最近活动
   * 文档约定：page、pageSize 必填；filters.type 可选（null 表示全部类型）
   */
  recentActivities: (body?: Record<string, unknown>) =>
    request.post('/api/dashboard/recent-activities', {
      page: 1,
      pageSize: 10,
      filters: { type: null },
      ...(body ?? {}),
    }),
  /**
   * 兼容旧的 dashboardApi.getStats 调用：
   * 内部并行调用 summary + recent-activities，并组装为 { stats, recentActivities }
   */
  getStats: async (body?: Record<string, unknown>) => {
    const recentBody = {
      page: 1,
      pageSize: 10,
      filters: { type: null as string | null },
      ...(body ?? {}),
    }
    const [summaryRes, activitiesRes, distributionRes] = await Promise.all([
      request.post('/api/dashboard/summary', { dummy: true, ...(body ?? {}) }),
      request.post('/api/dashboard/recent-activities', recentBody),
      request.post('/api/dashboard/distribution', { dummy: true, ...(body ?? {}) }),
    ])
    const stats = (summaryRes as any)?.data ?? summaryRes
    const activitiesRaw = (activitiesRes as any)?.data ?? activitiesRes
    const recentActivities = Array.isArray((activitiesRaw as any)?.items)
      ? (activitiesRaw as any).items
      : Array.isArray(activitiesRaw)
        ? activitiesRaw
        : []
    const distribution = (distributionRes as any)?.data ?? distributionRes
    return { stats, recentActivities, distribution }
  },
}

/** 资产列表请求参数：分页 + 筛选 + 排序 */
export interface AssetsListParams {
  page?: number
  pageSize?: number
  filters?: {
    keyword?: string
    type?: string
    risk?: string
    status?: string
  }
  sort?: {
    field?: string
    order?: string
  }
}

/** 资产产品：文档约定均为 POST */
export const assetApi = {
  /** POST /api/assets/list - 分页查询资产产品列表，带筛选与统计 */
  list: (body?: AssetsListParams | Record<string, unknown>) =>
    request.post('/api/assets/list', body ?? {}),
  /** POST /api/assets/detail - Asset Detail */
  detail: (body: { id: string } | Record<string, unknown>) => request.post('/api/assets/detail', body),
  /** POST /api/assets/create - Asset Create */
  create: (data: Record<string, unknown>) => request.post('/api/assets/create', data),
  /** POST /api/assets/update - Asset Update */
  update: (data: Record<string, unknown>) => request.post('/api/assets/update', data),
  /** POST /api/assets/delete - Asset Delete */
  delete: (body: { id: string } | Record<string, unknown>) => request.post('/api/assets/delete', body),
  /** POST /api/assets/linked-data-sources - 查询资产产品关联的资产数据列表（分页） */
  linkedDataSources: (body: {
    assetProductId: string
    page?: number
    pageSize?: number
    keyword?: string
    type?: string
    status?: string
  }) => request.post('/api/assets/linked-data-sources', body),
  /** POST /api/assets/link-data-sources - 创建资产产品与资产数据的关联关系 */
  linkDataSources: (body: { assetProductId: string; dataSourceIds: string[] }) =>
    request.post('/api/assets/link-data-sources', body),
  /** POST /api/assets/unlink-data-sources - 删除资产产品与资产数据的关联关系（仅删关联） */
  unlinkDataSources: (body: { assetProductId: string; dataSourceIds: string[] }) =>
    request.post('/api/assets/unlink-data-sources', body),
  /** 兼容旧调用：getAssets → list，支持分页筛选参数 */
  getAssets: (params?: AssetsListParams | Record<string, unknown>) =>
    request.post('/api/assets/list', params ?? {}),
  getAsset: (id: string) => request.post('/api/assets/detail', { id }),
  createAsset: (data: Record<string, unknown>) => request.post('/api/assets/create', data),
  updateAsset: (id: string, data: Record<string, unknown>) =>
    request.post('/api/assets/update', { ...data, id }),
  deleteAsset: (id: string) => request.post('/api/assets/delete', { id }),
}

/**
 * 资产数据筛选条件（与 backend-api-dev §4.1 / §4.2 / §4.5 中 `filters` 一致）
 * summary 与列表使用同一套条件聚合；export 的 filters 与列表相同。
 */
export interface DataSourcesFilters {
  type?: string | null
  status?: string | null
  keyword?: string | null
  dateFrom?: string | null
  dateTo?: string | null
  source?: string[] | null
  customer?: string[] | null
  settlementOrg?: string[] | null
  productName?: string[] | null
  contractTotalAmountMin?: number | null
  contractTotalAmountMax?: number | null
  contractCreatedFrom?: string | null
  contractCreatedTo?: string | null
  receiptDateFrom?: string | null
  receiptDateTo?: string | null
  /** 部分收款：是 | 否；不传表示全部 */
  partialReceipt?: string | null
  /** 业务分类多选：货转 | 物流 | 自提 | 信息 */
  rwaCategory?: string[] | null
  /** 高级查询：groupLogic、groups[].innerLogic、rows（field/operator/value） */
  advancedQuery?: Record<string, unknown> | null
}

/** 常用筛选下拉字段（POST /api/data-sources/filter-field-options） */
export type DataSourceFilterField = 'source' | 'customer' | 'settlementOrg' | 'productName'

export interface DataSourceFilterFieldOptionsBody {
  field: DataSourceFilterField
  keyword?: string
  limit?: number
}

/** 解析 filter-field-options 响应 → 去重值列表 */
export function unwrapFilterFieldOptions(res: unknown): string[] {
  if (res == null) return []
  const root = res as Record<string, unknown>
  const payload = root.data !== undefined ? root.data : root
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return []
  const items = (payload as Record<string, unknown>).items
  if (!Array.isArray(items)) return []
  return items.map(String).map((s) => s.trim()).filter(Boolean)
}

export interface DataSourcesListBody {
  page: number
  pageSize: number
  filters?: DataSourcesFilters
  sort?: {
    field?: string
    order?: 'asc' | 'desc'
  }
}

export interface DataSourcesSummaryBody {
  filters?: DataSourcesFilters
  dummy?: boolean
}

/** POST /api/data-sources/summary 响应 data 字段 */
export interface DataSourcesSummaryData {
  totalValue: number
  totalUnreceivedAmount: number
  totalPriceTaxTotal: number
  totalReceiptAmount: number
  availableValue: number
  totalCount: number
  availableCount: number
}

export interface DataSourcesExportBody {
  mode?: 'filtered' | 'selected'
  ids?: string[]
  filters?: DataSourcesFilters
  format?: 'csv' | string
  columns?: string[]
}

export interface ExportLogsListBody {
  page?: number
  pageSize?: number
  filters?: {
    keyword?: string
    module?: string
    status?: string
    dateFrom?: string
    dateTo?: string
  }
}

/** 与 `unwrapKingdeeTabRows` 写入一致：金蝶 Tab `data.item` 单头 + `data.items` 分录挂载在行上，供详情弹框「明细」Tab 使用 */
export const KINGDEE_TAB_LINE_ITEMS_KEY = '_kingdeeTabLineItems'

/** 应收单等 Tab 若 `data` 顶层另有收款计划数组，一并挂到行上供「收款计划」Tab 使用 */
export const KINGDEE_TAB_PLAN_ITEMS_KEY = '_kingdeeTabPlanItems'

const PLAN_PAYLOAD_ARRAY_KEYS = [
  'rec_plans',
  'recPlans',
  'receiptPlans',
  'receipt_plan',
  'receipt_plan_entry',
  'receipt_plan_details',
  'invoice_rec_plan',
  'invoicePlans',
  'plans',
  'plan_list',
  'planList',
  'freceiptplan',
  'freceiptplanentry',
  'sal_invoice_rec_plan',
  'ar_receivable_rec_plan',
] as const

/** 将 Tab 响应里与单头同级的 `items`、收款计划数组等合并进表格行 */
function mergeTabPayloadOntoMaster(master: Record<string, unknown>, payload: Record<string, unknown>): Record<string, unknown> {
  let out: Record<string, unknown> = { ...master }
  if (Array.isArray(payload.items)) {
    out = { ...out, [KINGDEE_TAB_LINE_ITEMS_KEY]: payload.items as Record<string, unknown>[] }
  }
  for (const pk of PLAN_PAYLOAD_ARRAY_KEYS) {
    const p = payload[pk]
    if (
      Array.isArray(p) &&
      p.length > 0 &&
      p.every((x) => x != null && typeof x === 'object' && !Array.isArray(x))
    ) {
      return { ...out, [KINGDEE_TAB_PLAN_ITEMS_KEY]: p as Record<string, unknown>[] }
    }
  }
  const planKeyRe =
    /recplan|receipt.*plan|收款计划|invoice.*plan|plan.*entry|freceiptplan|rec_plan|payplan|sal_invoice.*plan/i
  for (const [k, v] of Object.entries(payload)) {
    if (k === 'item' || k === 'items' || k === 'attachments' || k === 'fieldReference' || k === 'attachmentsCollection') continue
    if (
      planKeyRe.test(k) &&
      Array.isArray(v) &&
      v.length > 0 &&
      v.every((x) => x != null && typeof x === 'object' && !Array.isArray(x))
    ) {
      return { ...out, [KINGDEE_TAB_PLAN_ITEMS_KEY]: v as Record<string, unknown>[] }
    }
  }
  return out
}

/**
 * 金蝶 Tab 接口响应 → 行数组（兼容 axios 拦截器返回的整包 `{ data }` 与 `data.items` 等）
 * - 合同/订单/应收等：`data.item` 为单头、`data.items` 为分录 → **一行单头** `[{ ...item, _kingdeeTabLineItems }]`（见 api_kingdee_data_source_tabs.md）
 * - 销售出库/物流：`{ deliveryNotices, salesOutstocks }` → 合并为行数组；出库元素含 `item`+`items` 时展开单头字段并写入 `_kingdeeTabLineItems`
 */
export function unwrapKingdeeTabRows(res: unknown): Record<string, unknown>[] {
  if (res == null) return []
  const root = res as Record<string, unknown>
  const payload = root.data !== undefined ? root.data : root
  if (Array.isArray(payload)) return payload as Record<string, unknown>[]
  if (payload && typeof payload === 'object') {
    const o = payload as Record<string, unknown>

    /** 物流/销售出库 Tab：`{ deliveryNotices, salesOutstocks }`（见 kingdee-tab/sales-outstock） */
    const deliveryNotices = o.deliveryNotices
    const salesOutstocks = o.salesOutstocks
    if (Array.isArray(deliveryNotices) || Array.isArray(salesOutstocks)) {
      const merged: Record<string, unknown>[] = []
      for (const r of Array.isArray(deliveryNotices) ? deliveryNotices : []) {
        if (r && typeof r === 'object' && !Array.isArray(r)) {
          merged.push({ ...(r as Record<string, unknown>), _logisticsDocSource: 'delivery_notice' })
        }
      }
      for (const r of Array.isArray(salesOutstocks) ? salesOutstocks : []) {
        if (r && typeof r === 'object' && !Array.isArray(r)) {
          const obj = r as Record<string, unknown>
          const head =
            obj.item != null && typeof obj.item === 'object' && !Array.isArray(obj.item)
              ? (obj.item as Record<string, unknown>)
              : {}
          const lineItems = Array.isArray(obj.items) ? (obj.items as Record<string, unknown>[]) : []
          merged.push({
            ...obj,
            ...head,
            [KINGDEE_TAB_LINE_ITEMS_KEY]: lineItems,
            _logisticsDocSource: 'sales_outstock',
          })
        }
      }
      return merged
    }

    const itemHead = o.item
    if (itemHead != null && typeof itemHead === 'object' && !Array.isArray(itemHead)) {
      return [mergeTabPayloadOntoMaster(itemHead as Record<string, unknown>, o)]
    }

    for (const k of ['rows', 'list', 'records', 'items'] as const) {
      const x = o[k]
      if (Array.isArray(x)) return x as Record<string, unknown>[]
    }

    for (const solo of ['item', 'record', 'row', 'detail'] as const) {
      const one = o[solo]
      if (one && typeof one === 'object' && !Array.isArray(one)) {
        return [one as Record<string, unknown>]
      }
    }
    const inner = o.data
    if (Array.isArray(inner)) return inner as Record<string, unknown>[]
    if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
      const innerObj = inner as Record<string, unknown>
      const innerItem = innerObj.item
      if (innerItem != null && typeof innerItem === 'object' && !Array.isArray(innerItem)) {
        return [mergeTabPayloadOntoMaster(innerItem as Record<string, unknown>, innerObj)]
      }
      for (const solo of ['item', 'record', 'row'] as const) {
        const one = innerObj[solo]
        if (one && typeof one === 'object' && !Array.isArray(one)) {
          return [one as Record<string, unknown>]
        }
      }
    }
  }
  return []
}

/**
 * 金蝶 Tab 接口响应中的 `data.attachments`（与主表行按 `bill_no` 匹配，见接口 fieldReference 说明）
 */
export function unwrapKingdeeTabAttachments(res: unknown): Record<string, unknown>[] {
  if (res == null) return []
  const root = res as Record<string, unknown>
  const payload = root.data !== undefined ? root.data : root
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return []
  const att = (payload as Record<string, unknown>).attachments
  if (!Array.isArray(att)) return []
  return att.filter((x) => x && typeof x === 'object' && !Array.isArray(x)) as Record<string, unknown>[]
}

/** 数据源：POST /api/data-sources/* （资产数据页使用，契约见 backend-api-dev §4） */
export const dataSourcesApi = {
  /** POST /api/data-sources/summary — filters 与列表一致时汇总 totalValue 等 */
  summary: (body?: DataSourcesSummaryBody | Record<string, unknown>) =>
    request.post('/api/data-sources/summary', body ?? {}),
  /** POST /api/data-sources/list */
  list: (body?: Partial<DataSourcesListBody> | Record<string, unknown>) =>
    request.post('/api/data-sources/list', body ?? {}),
  /** POST /api/data-sources/filter-field-options — 来源/客户/结算组织下拉选项 */
  filterFieldOptions: (body: DataSourceFilterFieldOptionsBody) =>
    request.post('/api/data-sources/filter-field-options', body),
  /** POST /api/data-sources/detail — 含 contracts/orders/receipts/logistics/invoices */
  detail: (body: { id: string } | Record<string, unknown>) =>
    request.post('/api/data-sources/detail', body),
  /** POST /api/data-sources/delete */
  delete: (body: { id: string } | Record<string, unknown>) =>
    request.post('/api/data-sources/delete', body),
  /** POST /api/data-sources/update — 修改业务字段；status=deleted 时同步解除产品关联 */
  update: (body: { id: string } & Record<string, unknown>) =>
    request.post('/api/data-sources/update', body),
  /**
   * POST /api/data-sources/export
   * format=csv 时为 CSV 文件流（blob）；否则可能返回 JSON（含 fileUrl）
   */
  export: (body?: DataSourcesExportBody | Record<string, unknown>) =>
    request.post('/api/data-sources/export', body ?? {}, { responseType: 'blob' }),
  /** POST /api/data-sources/export-template — 下载导入模板 CSV */
  exportTemplate: () =>
    request.post('/api/data-sources/export-template', {}, { responseType: 'blob' }),
  /** POST /api/data-sources/import — multipart 上传 CSV，按模板导入资产数据 */
  import: (formData: FormData) =>
    request.post('/api/data-sources/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  /**
   * 金蝶关联 Tab：POST body 与 `/api/data-sources/detail` 返回字段对齐
   * - `id`：数据项 id
   * - `ordernumber` / `bill_no`：由详情接口 payload 解析（兼容 camelCase / snake_case 别名）
   * - **销售合同**：`/kingdee-tab/sales-contract` 仅需 `id` + `ordernumber`，不传 `bill_no`
   */
  kingdeeTabSalesContract: (body: { id: string; ordernumber: string }) =>
    request.post('/api/data-sources/kingdee-tab/sales-contract', body),
  kingdeeTabSalesOrder: (body: { id: string; ordernumber: string; bill_no: string }) =>
    request.post('/api/data-sources/kingdee-tab/sales-order', body),
  kingdeeTabReceiveBills: (body: { id: string; ordernumber: string; bill_no: string }) =>
    request.post('/api/data-sources/kingdee-tab/receive-bills', body),
  kingdeeTabSalesOutstock: (body: { id: string; ordernumber: string; bill_no: string }) =>
    request.post('/api/data-sources/kingdee-tab/sales-outstock', body),
  /** 销售发票：响应体与合同 Tab 类似，可含 `data.attachments`（按 bill_no 合并） */
  kingdeeTabSalesInvoice: (body: { id: string; ordernumber: string; bill_no: string }) =>
    request.post('/api/data-sources/kingdee-tab/ar-receivables', body),
  /** 合同上链：传销售订单号与合同单据号 */
  contractChainUpload: (body: { ordernumber: string; bill_no: string }) =>
    request.post('/api/data-sources/contract-chain/upload', body),
}

export const exportLogsApi = {
  list: (body?: ExportLogsListBody | Record<string, unknown>) =>
    request.post('/api/export-logs/list', body ?? {}),
  export: (body?: ExportLogsListBody | Record<string, unknown>) =>
    request.post('/api/export-logs/export', body ?? {}, { responseType: 'blob' }),
}

export interface ImportLogsListBody {
  page?: number
  pageSize?: number
  filters?: {
    keyword?: string
    status?: string
    dateFrom?: string
    dateTo?: string
  }
}

export const importLogsApi = {
  list: (body?: ImportLogsListBody | Record<string, unknown>) =>
    request.post('/api/import-logs/list', body ?? {}),
  status: (body: { importId: string }) => request.post('/api/import-logs/status', body),
  download: (body: { importId: string; type: 'success' | 'failure' }) =>
    request.post('/api/import-logs/download', body, { responseType: 'blob' }),
}

/** 常用筛选场景（资产数据页）：按登录用户存储于服务端 */
export const dataSourceFilterSchemesApi = {
  /** POST /api/data-sources/filter-schemes/list */
  list: (body?: Record<string, unknown>) =>
    request.post('/api/data-sources/filter-schemes/list', body ?? {}),
  /** POST /api/data-sources/filter-schemes/save — id 可选，有则更新 */
  save: (body: {
    id?: string
    name: string
    snapshot: Record<string, unknown>
    advancedQuery: Record<string, unknown>
  }) => request.post('/api/data-sources/filter-schemes/save', body),
  /** POST /api/data-sources/filter-schemes/delete */
  delete: (body: { id: string }) => request.post('/api/data-sources/filter-schemes/delete', body),
}

/** 资产数据：/api/data */
export const dataApi = {
  getData: (params?: Record<string, unknown>) => request.get('/api/data', { params }),
  importData: (formData: FormData) =>
    request.post('/api/data/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  exportData: (params?: Record<string, unknown>) =>
    request.get('/api/data/export', { params, responseType: 'blob' }),
  getStatistics: (params?: Record<string, unknown>) => request.get('/api/data/statistics', { params }),
}

/** 分析：文档约定均为 POST */
export const analyticsApi = {
  /** POST /api/analytics/indicators */
  indicators: (body?: Record<string, unknown>) => request.post('/api/analytics/indicators', body ?? {}),
  /** POST /api/analytics/asset-distribution */
  assetDistribution: (body?: Record<string, unknown>) => request.post('/api/analytics/asset-distribution', body ?? {}),
  /** POST /api/analytics/trend-analysis */
  trendAnalysis: (body?: Record<string, unknown>) => request.post('/api/analytics/trend-analysis', body ?? {}),
  /** POST /api/analytics/comparison-analysis */
  comparisonAnalysis: (body?: Record<string, unknown>) => request.post('/api/analytics/comparison-analysis', body ?? {}),
  /** POST /api/analytics/amount-dimension */
  amountDimension: (body?: Record<string, unknown>) => request.post('/api/analytics/amount-dimension', body ?? {}),
  /** POST /api/analytics/reports/list */
  reportsList: (body?: Record<string, unknown>) => request.post('/api/analytics/reports/list', body ?? {}),
  /** POST /api/analytics/reports/create */
  reportsCreate: (body?: Record<string, unknown>) => request.post('/api/analytics/reports/create', body ?? {}),
  /** POST /api/analytics/reports/detail */
  reportsDetail: (body: { id: string } | Record<string, unknown>) => request.post('/api/analytics/reports/detail', body),
  /** POST /api/analytics/reports/download */
  reportsDownload: (body: { id: string } | Record<string, unknown>) =>
    request.post('/api/analytics/reports/download', body, { responseType: 'blob' }),
  /** 兼容旧调用 */
  getReports: (params?: Record<string, unknown>) => request.post('/api/analytics/reports/list', params ?? {}),
  generateReport: (data: Record<string, unknown>) => request.post('/api/analytics/reports/create', data),
  downloadReport: (id: string) =>
    request.post('/api/analytics/reports/download', { id }, { responseType: 'blob' }),
  getAnalyticsData: (params?: Record<string, unknown>) =>
    request.post('/api/analytics/indicators', params ?? {}),
}

/** 用户 / 个人资料：改用 /api/settings/profile 与 /api/settings/security */
export const userApi = {
  /** POST /api/settings/profile/get */
  getProfile: (body?: Record<string, unknown>) =>
    request.post('/api/settings/profile/get', body ?? {}),
  /** POST /api/settings/profile/update */
  updateProfile: (data: Record<string, unknown>) =>
    request.post('/api/settings/profile/update', data),
  /** POST /api/settings/security/change-password */
  changePassword: (data: Record<string, unknown>) =>
    request.post('/api/settings/security/change-password', data),
}

/** 系统设置：改用 /api/settings/system */
export const systemApi = {
  /** POST /api/settings/system/get */
  getSettings: (body?: Record<string, unknown>) =>
    request.post('/api/settings/system/get', body ?? {}),
  /** POST /api/settings/system/update */
  updateSettings: (data: Record<string, unknown>) =>
    request.post('/api/settings/system/update', data),
  /** POST /api/settings/system/info */
  getStatus: (body?: Record<string, unknown>) =>
    request.post('/api/settings/system/info', body ?? {}),
  /** POST /api/settings/system/backup */
  backupData: (body?: Record<string, unknown>) =>
    request.post('/api/settings/system/backup', body ?? {}),
  /** POST /api/settings/system/check-update */
  checkUpdate: (body?: Record<string, unknown>) =>
    request.post('/api/settings/system/check-update', body ?? {}),
  /** POST /api/settings/system/reset-defaults */
  resetDefaults: (body?: Record<string, unknown>) =>
    request.post('/api/settings/system/reset-defaults', body ?? {}),
  /** 兼容旧的 restoreData，后端如无该接口可忽略调用 */
  restoreData: (data: Record<string, unknown>) =>
    request.post('/api/system/restore', data),
}

/** 设置中心：通知 & 安全类接口 */
export const settingsApi = {
  /** 通知设置：GET/UPDATE */
  notificationsGet: (body?: Record<string, unknown>) =>
    request.post('/api/settings/notifications/get', body ?? {}),
  notificationsUpdate: (data: Record<string, unknown>) =>
    request.post('/api/settings/notifications/update', data),
  /** 安全设置：GET/UPDATE/会话管理 */
  securityGet: (body?: Record<string, unknown>) =>
    request.post('/api/settings/security/get', body ?? {}),
  securityUpdate: (data: Record<string, unknown>) =>
    request.post('/api/settings/security/update', data),
  securitySessions: (body?: Record<string, unknown>) =>
    request.post('/api/settings/security/sessions', body ?? {}),
  securityRevokeSession: (data: Record<string, unknown>) =>
    request.post('/api/settings/security/revoke-session', data),
}

/** 通知：/api/notifications */
export const notificationApi = {
  getNotifications: (params?: Record<string, unknown>) =>
    request.get('/api/notifications', { params }),
  markAsRead: (id: string) => request.put(`/api/notifications/${id}/read`),
  markAllAsRead: () => request.put('/api/notifications/read-all'),
  deleteNotification: (id: string) => request.delete(`/api/notifications/${id}`),
  clearNotifications: () => request.delete('/api/notifications'),
}

// ========== 角色与权限管理 ==========

/** 权限类型：菜单权限控制侧栏/路由可见，按钮权限控制页面内操作按钮 */
export type PermissionType = 'menu' | 'button'

/** 权限定义（含类型：菜单 vs 按钮） */
export const PERMISSION_LIST: readonly { code: string; name: string; module: string; type: PermissionType }[] = [
  { code: 'dashboard:view', name: '仪表板', module: '仪表板', type: 'menu' },
  { code: 'data:view', name: '资产数据-访问', module: '资产数据', type: 'menu' },
  { code: 'data:export', name: '资产数据-导出', module: '资产数据', type: 'button' },
  { code: 'import_logs:list', name: '导入日志-查看', module: '导入日志', type: 'menu' },
  { code: 'import_logs:download', name: '导入日志-下载', module: '导入日志', type: 'button' },
  { code: 'export_logs:list', name: '导出日志-查看', module: '导出日志', type: 'menu' },
  { code: 'export_logs:export', name: '导出日志-导出', module: '导出日志', type: 'button' },
  { code: 'assets:view', name: '资产产品-访问', module: '资产产品', type: 'menu' },
  { code: 'assets:create', name: '资产产品-新增', module: '资产产品', type: 'button' },
  { code: 'assets:edit', name: '资产产品-编辑', module: '资产产品', type: 'button' },
  { code: 'assets:delete', name: '资产产品-删除', module: '资产产品', type: 'button' },
  { code: 'analytics:view', name: '分析报告-访问', module: '分析报告', type: 'menu' },
  { code: 'analytics:export', name: '分析报告-导出', module: '分析报告', type: 'button' },
  { code: 'settings:view', name: '系统设置-访问', module: '系统设置', type: 'menu' },
  { code: 'settings:edit', name: '系统设置-编辑', module: '系统设置', type: 'button' },
  { code: 'users:view', name: '账号管理-访问', module: '系统管理', type: 'menu' },
  { code: 'users:create', name: '账号管理-新增', module: '系统管理', type: 'button' },
  { code: 'users:edit', name: '账号管理-编辑', module: '系统管理', type: 'button' },
  { code: 'users:delete', name: '账号管理-删除', module: '系统管理', type: 'button' },
  { code: 'users:resetPwd', name: '账号管理-重置密码', module: '系统管理', type: 'button' },
  { code: 'users:manage', name: '账号管理-全部', module: '系统管理', type: 'menu' },
  { code: 'roles:view', name: '角色管理-访问', module: '系统管理', type: 'menu' },
  { code: 'roles:create', name: '角色管理-新增', module: '系统管理', type: 'button' },
  { code: 'roles:edit', name: '角色管理-编辑', module: '系统管理', type: 'button' },
  { code: 'roles:delete', name: '角色管理-删除', module: '系统管理', type: 'button' },
  { code: 'roles:manage', name: '角色管理-全部', module: '系统管理', type: 'menu' },
]

export type PermissionCode = (typeof PERMISSION_LIST)[number]['code']

/** 仅菜单权限码（用于侧栏/路由） */
export const MENU_PERMISSION_CODES = PERMISSION_LIST.filter((p) => p.type === 'menu').map((p) => p.code)

export interface RoleItem {
  id: string
  name: string
  code: string
  description?: string
  permissions: string[]
  status?: 'active' | 'disabled'
  createdAt?: string
  updatedAt?: string
}

export interface UserAccountItem {
  id: string
  username: string
  email?: string
  nickname?: string
  phone?: string
  roles: { id: string; name: string; code: string }[]
  status?: 'active' | 'disabled'
  lastLoginAt?: string
  createdAt?: string
}

/** POST /api/users/create 入参 */
export interface UserCreateParams {
  username: string
  password: string
  email?: string
  nickname?: string
  phone?: string
  status?: 'active' | 'disabled'
  roleIds?: string[]
}

/** POST /api/users/update 入参 */
export interface UserUpdateParams {
  id: string
  email?: string
  nickname?: string
  phone?: string
  status?: string
  roleIds?: string[]
}

/** POST /api/users/delete 入参 */
export interface UserDeleteParams {
  id: string
}

/** POST /api/users/reset-password 入参 */
export interface UserResetPasswordParams {
  id: string
  password: string
}

/** 列表请求通用入参（与后端文档 0.4 分页约定一致） */
export interface ListParams {
  page?: number
  pageSize?: number
  filters?: { keyword?: string; [k: string]: unknown }
  [k: string]: unknown
}

/** 列表响应 data 结构（与后端文档 0.4 一致） */
export interface ListResponseData<T> {
  items: T[]
  total?: number
  page?: number
  pageSize?: number
}

/** POST /api/roles/list 入参 */
export interface RoleListParams {
  keyword?: string
  page?: number
  pageSize?: number
}

/** POST /api/roles/create 入参 */
export interface RoleCreateParams {
  name: string
  code: string
  description?: string
  permissions?: string[]
  status?: 'active' | 'disabled'
}

/** POST /api/roles/update 入参 */
export interface RoleUpdateParams {
  id: string
  name?: string
  code?: string
  description?: string
  permissions?: string[]
  status?: string
}

/** POST /api/roles/delete 入参 */
export interface RoleDeleteParams {
  id: string
}

/** 角色/权限配置用：菜单与按钮列表项（/api/menus/list-for-role） */
export interface MenuForRoleItem {
  code: string
  name: string
  module: string
  type: 'menu' | 'button'
}

/** 菜单列表接口（用于角色新增/编辑时拉取全部可分配权限，与后端文档一致：全部 POST） */
export const menusApi = {
  /** POST /api/menus/list-for-role - 角色可分配的菜单与按钮列表 */
  listForRole: (body?: Record<string, unknown>) =>
    request.post<{ success?: boolean; code?: string; message?: string; data?: { items?: MenuForRoleItem[] } }>(
      '/api/menus/list-for-role',
      body ?? {}
    ),
}

/** 角色管理 API（与 backend-api-dev.md 约定一致：全部 POST） */
export const roleApi = {
  /** POST /api/roles/list - 角色列表，入参：keyword?, page?, pageSize? */
  list: (body?: RoleListParams) =>
    request.post<{ success?: boolean; code?: string; message?: string; data?: ListResponseData<unknown> }>(
      '/api/roles/list',
      body ?? {}
    ),
  /** POST /api/roles/detail - 角色详情 */
  detail: (body: { id: string }) => request.post('/api/roles/detail', body),
  /** POST /api/roles/create - 创建角色 */
  create: (data: RoleCreateParams) => request.post('/api/roles/create', data),
  /** POST /api/roles/update - 更新角色 */
  update: (data: RoleUpdateParams) => request.post('/api/roles/update', data),
  /** POST /api/roles/delete - 删除角色 */
  delete: (body: RoleDeleteParams) => request.post('/api/roles/delete', body),
}

/** POST /api/users/list 入参 */
export interface UserListParams {
  keyword?: string
  page?: number
  pageSize?: number
}

/** 账号管理 API（用户管理，与 backend-api-dev.md 约定一致：全部 POST） */
export const userManageApi = {
  /** POST /api/users/list - 用户账号列表，入参：keyword?, page?, pageSize? */
  list: (body?: UserListParams) =>
    request.post<{ success?: boolean; code?: string; message?: string; data?: ListResponseData<unknown> }>(
      '/api/users/list',
      body ?? {}
    ),
  /** POST /api/users/detail - 用户详情 */
  detail: (body: { id: string }) => request.post('/api/users/detail', body),
  /** POST /api/users/create - 创建用户，入参：username, password, email?, nickname?, phone?, status?, roleIds? */
  create: (data: UserCreateParams) => request.post('/api/users/create', data),
  /** POST /api/users/update - 更新用户，入参：id, email?, nickname?, phone?, status?, roleIds? */
  update: (data: UserUpdateParams) => request.post('/api/users/update', data),
  /** POST /api/users/delete - 删除用户，入参：id */
  delete: (body: UserDeleteParams) => request.post('/api/users/delete', body),
  /** POST /api/users/reset-password - 重置密码，入参：id, password */
  resetPassword: (data: UserResetPasswordParams) =>
    request.post('/api/users/reset-password', data),
}

/** 上传：/api/upload */
export const uploadApi = {
  uploadFile: (formData: FormData) =>
    request.post('/api/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  getUploadProgress: (id: string) => request.get(`/api/upload/${id}/progress`),
  cancelUpload: (id: string) => request.delete(`/api/upload/${id}`),
}

export default request
